/**
 * Shell — Top-level layout wrapper for all LoanKnow portals
 * 
 * Replaces the old Tabler-based AppLayout with a modern structure:
 * - Slim top navigation bar
 * - Full-width content area with max-width constraint
 * - No sidebar by default (progressive disclosure model)
 */
import { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router';
import { LogOut, ChevronDown, Menu, X } from 'lucide-react';

/* ── TopNav ────────────────────────────────────────────────────── */
const TopNav = ({ role, navItems = [], user, onLogout }) => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const isActive = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Filter out dividers and headers for top nav
  const links = navItems.filter(item => !item.divider && !item.header);

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || 'U';

  const roleBadgeColors = {
    admin:       { bg: 'var(--lk-danger-50)', color: 'var(--lk-danger-700)' },
    underwriter: { bg: 'var(--lk-primary-50)', color: 'var(--lk-primary-700)' },
    lender:      { bg: 'var(--lk-info-50)', color: 'var(--lk-info-600)' },
    borrower:    { bg: 'var(--lk-success-50)', color: 'var(--lk-success-700)' },
    referral:    { bg: 'var(--lk-warning-50)', color: 'var(--lk-warning-700)' },
  };

  const badge = roleBadgeColors[role] || roleBadgeColors.borrower;

  return (
    <nav style={{
      position: 'sticky',
      top: 0,
      zIndex: 'var(--lk-z-sticky)',
      height: 'var(--lk-nav-height)',
      background: 'var(--lk-bg-nav)',
      borderBottom: '1px solid var(--lk-border)',
      display: 'flex',
      alignItems: 'center',
      padding: '0 var(--lk-space-6)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    }}>
      {/* Logo */}
      <Link to="/" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--lk-space-2)',
        textDecoration: 'none',
        marginRight: 'var(--lk-space-8)',
        flexShrink: 0,
      }}>
        <div style={{
          width: 28,
          height: 28,
          borderRadius: 'var(--lk-radius-md)',
          background: 'var(--lk-primary-600)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 700,
          fontSize: '13px',
          letterSpacing: '-0.02em',
        }}>
          LK
        </div>
        <span style={{
          fontWeight: 600,
          fontSize: 'var(--lk-text-md)',
          color: 'var(--lk-text-primary)',
          letterSpacing: '-0.01em',
        }}>
          LoanKnow
        </span>
      </Link>

      {/* Desktop Nav Links */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--lk-space-1)',
        flex: 1,
        overflow: 'hidden',
      }} className="lk-nav-desktop">
        {links.map(item => (
          <Link
            key={item.id}
            to={item.path}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: 'var(--lk-radius-sm)',
              fontSize: 'var(--lk-text-sm)',
              fontWeight: isActive(item.path) ? 500 : 400,
              color: isActive(item.path) ? 'var(--lk-text-primary)' : 'var(--lk-text-tertiary)',
              background: isActive(item.path) ? 'var(--lk-bg-surface-2)' : 'transparent',
              textDecoration: 'none',
              transition: 'all var(--lk-duration-fast) var(--lk-ease)',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => {
              if (!isActive(item.path)) {
                e.currentTarget.style.color = 'var(--lk-text-secondary)';
                e.currentTarget.style.background = 'var(--lk-bg-surface-2)';
              }
            }}
            onMouseLeave={e => {
              if (!isActive(item.path)) {
                e.currentTarget.style.color = 'var(--lk-text-tertiary)';
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            {item.label}
            {item.badge && (
              <span style={{
                fontSize: '11px',
                fontWeight: 600,
                padding: '1px 6px',
                borderRadius: 'var(--lk-radius-full)',
                background: 'var(--lk-danger-50)',
                color: 'var(--lk-danger-600)',
                lineHeight: '16px',
              }}>
                {item.badge}
              </span>
            )}
          </Link>
        ))}
      </div>

      {/* Right side: role badge + user menu */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--lk-space-3)',
        marginLeft: 'auto',
        flexShrink: 0,
      }}>
        {/* Role badge */}
        {role && (
          <span style={{
            fontSize: '11px',
            fontWeight: 600,
            padding: '3px 8px',
            borderRadius: 'var(--lk-radius-full)',
            background: badge.bg,
            color: badge.color,
            textTransform: 'capitalize',
          }}>
            {role}
          </span>
        )}

        {/* User dropdown */}
        <div ref={userMenuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--lk-space-2)',
              padding: '4px 8px 4px 4px',
              border: '1px solid transparent',
              borderRadius: 'var(--lk-radius-md)',
              background: userMenuOpen ? 'var(--lk-bg-surface-2)' : 'transparent',
              cursor: 'pointer',
              transition: 'all var(--lk-duration-fast) var(--lk-ease)',
            }}
            onMouseEnter={e => {
              if (!userMenuOpen) e.currentTarget.style.background = 'var(--lk-bg-surface-2)';
            }}
            onMouseLeave={e => {
              if (!userMenuOpen) e.currentTarget.style.background = 'transparent';
            }}
          >
            <div className="lk-avatar lk-avatar-sm">{initials}</div>
            <ChevronDown size={14} color="var(--lk-text-tertiary)" />
          </button>

          {/* Dropdown */}
          {userMenuOpen && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              right: 0,
              minWidth: 200,
              background: 'var(--lk-bg-elevated)',
              border: '1px solid var(--lk-border)',
              borderRadius: 'var(--lk-radius-lg)',
              boxShadow: 'var(--lk-shadow-lg)',
              padding: 'var(--lk-space-1)',
              zIndex: 'var(--lk-z-dropdown)',
              animation: 'lk-scale-in var(--lk-duration-fast) var(--lk-ease)',
            }}>
              {/* User info */}
              <div style={{
                padding: 'var(--lk-space-3) var(--lk-space-3)',
                borderBottom: '1px solid var(--lk-border)',
                marginBottom: 'var(--lk-space-1)',
              }}>
                <div style={{
                  fontSize: 'var(--lk-text-sm)',
                  fontWeight: 500,
                  color: 'var(--lk-text-primary)',
                }}>
                  {user?.name || 'User'}
                </div>
                {user?.email && (
                  <div style={{
                    fontSize: 'var(--lk-text-xs)',
                    color: 'var(--lk-text-tertiary)',
                    marginTop: 2,
                  }}>
                    {user.email}
                  </div>
                )}
              </div>

              {/* Logout */}
              <button
                onClick={() => { setUserMenuOpen(false); onLogout?.(); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--lk-space-2)',
                  width: '100%',
                  padding: 'var(--lk-space-2) var(--lk-space-3)',
                  border: 'none',
                  borderRadius: 'var(--lk-radius-sm)',
                  background: 'transparent',
                  color: 'var(--lk-text-secondary)',
                  fontSize: 'var(--lk-text-sm)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all var(--lk-duration-fast) var(--lk-ease)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--lk-bg-surface-2)';
                  e.currentTarget.style.color = 'var(--lk-text-primary)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--lk-text-secondary)';
                }}
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="lk-nav-mobile-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            border: 'none',
            borderRadius: 'var(--lk-radius-md)',
            background: 'transparent',
            color: 'var(--lk-text-secondary)',
            cursor: 'pointer',
          }}
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile nav overlay */}
      {mobileOpen && (
        <div style={{
          position: 'fixed',
          top: 'var(--lk-nav-height)',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'var(--lk-bg-surface-1)',
          zIndex: 'var(--lk-z-overlay)',
          padding: 'var(--lk-space-4)',
          animation: 'lk-slide-down var(--lk-duration-normal) var(--lk-ease)',
          overflowY: 'auto',
        }} className="lk-nav-mobile-panel">
          {links.map(item => (
            <Link
              key={item.id}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--lk-space-3)',
                padding: 'var(--lk-space-3) var(--lk-space-4)',
                borderRadius: 'var(--lk-radius-md)',
                fontSize: 'var(--lk-text-md)',
                fontWeight: isActive(item.path) ? 500 : 400,
                color: isActive(item.path) ? 'var(--lk-text-primary)' : 'var(--lk-text-secondary)',
                background: isActive(item.path) ? 'var(--lk-bg-surface-2)' : 'transparent',
                textDecoration: 'none',
                marginBottom: 'var(--lk-space-1)',
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
};

/* ── Shell ─────────────────────────────────────────────────────── */
const Shell = ({ role, navItems, user, onLogout, children }) => {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--lk-bg-page)',
    }}>
      <TopNav
        role={role}
        navItems={navItems}
        user={user}
        onLogout={onLogout}
      />
      <main style={{
        flex: 1,
        width: '100%',
        maxWidth: 'var(--lk-content-max)',
        margin: '0 auto',
        padding: 'var(--lk-space-8) var(--lk-space-6)',
      }}>
        {children || <Outlet />}
      </main>
    </div>
  );
};

/* ── Responsive CSS (injected once) ────────────────────────────── */
const styleId = 'lk-shell-responsive';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @media (max-width: 768px) {
      .lk-nav-desktop { display: none !important; }
      .lk-nav-mobile-toggle { display: flex !important; }
    }
    @media (min-width: 769px) {
      .lk-nav-mobile-panel { display: none !important; }
    }
  `;
  document.head.appendChild(style);
}

export { TopNav };
export default Shell;
